export type ScalarRange = [number, number];

export function isFiniteScalarRange(
  range: ScalarRange | null | undefined,
  eps = 1e-12,
): range is ScalarRange {
  if (!range) return false;
  const [min, max] = range;
  return (
    Number.isFinite(min) && Number.isFinite(max) && Math.abs(max - min) > eps
  );
}

export function computeScalarRange(
  values: ArrayLike<number> | null | undefined,
): ScalarRange | null {
  if (!values || values.length === 0) return null;

  let min = Infinity;
  let max = -Infinity;

  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (!Number.isFinite(value)) continue;

    if (value < min) min = value;
    if (value > max) max = value;
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  return isFiniteScalarRange([min, max]) ? [min, max] : null;
}

/**
 * Compute a percentile-based scalar range, ignoring extreme outliers.
 *
 * Sorts all finite values, then picks the value at `lowPercentile` (e.g. 1)
 * and `highPercentile` (e.g. 99).  Values outside this window are still
 * rendered but clamp to the nearest palette endpoint — preventing a single
 * outlier from collapsing the visible color spread.
 *
 * Falls back to `computeScalarRange` when too few values exist for
 * meaningful percentile bounds.
 */
export function computePercentileRange(
  values: ArrayLike<number> | null | undefined,
  lowPercentile = 1,
  highPercentile = 99,
): ScalarRange | null {
  if (!values || values.length === 0) return null;

  // Collect finite values into a sorted array
  const finite: number[] = [];
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (Number.isFinite(v)) finite.push(v);
  }
  if (finite.length < 4) return computeScalarRange(values);

  finite.sort((a, b) => a - b);

  const n = finite.length;
  const loIdx = Math.max(0, Math.min(n - 1, Math.floor((lowPercentile / 100) * n)));
  const hiIdx = Math.max(0, Math.min(n - 1, Math.floor((highPercentile / 100) * n)));
  const range: ScalarRange = [finite[loIdx], finite[hiIdx]];

  return isFiniteScalarRange(range) ? range : null;
}

/**
 * Like `resolveScalarRange` but uses percentile-based bounds so outliers
 * don't compress the visible colour range.
 */
export function resolveRobustScalarRange(
  values: ArrayLike<number>,
  forcedRange?: ScalarRange | null,
  lowPercentile = 1,
  highPercentile = 99,
): ScalarRange | null {
  let range: ScalarRange | null;
  if (isFiniteScalarRange(forcedRange)) {
    range = forcedRange;
  } else {
    range = computePercentileRange(values, lowPercentile, highPercentile);
  }

  // Scale always starts from 0 — no negative values shown
  if (range && range[0] < 0) {
    range = [0, range[1]];
  }

  return range;
}

export function resolveScalarRange(
  values: ArrayLike<number>,
  forcedRange?: ScalarRange | null,
): ScalarRange | null {
  let range: ScalarRange | null;
  if (isFiniteScalarRange(forcedRange)) {
    range = forcedRange;
  } else {
    range = computeScalarRange(values);
  }

  // Scale always starts from 0 — no negative values shown
  if (range && range[0] < 0) {
    range = [0, range[1]];
  }

  return range;
}

// Cool-to-Warm diverging colormap stops (blue → white/gray → red).
// Classic VTK medical-visualization palette — perceptually uniform,
// good for scalar visualization.
const COOL_TO_WARM_STOPS: Array<[number, number, number, number]> = [
  [0.0, 0.231, 0.298, 0.753], // deep blue
  [0.25, 0.488, 0.572, 0.918], // lighter blue
  [0.5, 0.867, 0.867, 0.867], // white/gray (diverging midpoint)
  [0.75, 0.843, 0.4, 0.361], // light red / salmon
  [1.0, 0.706, 0.016, 0.15], // deep red
];

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lookupCoolToWarm(t: number): [number, number, number] {
  for (let i = 0; i < COOL_TO_WARM_STOPS.length - 1; i++) {
    const [t0, r0, g0, b0] = COOL_TO_WARM_STOPS[i];
    const [t1, r1, g1, b1] = COOL_TO_WARM_STOPS[i + 1];
    if (t >= t0 && t <= t1) {
      const s = t === t1 ? 1 : (t - t0) / (t1 - t0);
      return [lerp(r0, r1, s), lerp(g0, g1, s), lerp(b0, b1, s)];
    }
  }
  return [0.867, 0.867, 0.867]; // fallback gray
}

export function applyPaletteToMapper(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  vtk: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapper: any,
  range: ScalarRange,
) {
  const [min, max] = range;

  let lut = mapper.getLookupTable?.();

  if (!lut) {
    lut = vtk.vtkLookupTable.newInstance();
    mapper.setLookupTable?.(lut);
  }

  // Build RGBA color table (0-255 integers) for Cool-to-Warm.
  // Use lut.setTable() — the only reliable way for custom colormaps
  // in VTK.js. Do NOT call build()/forceBuild() after, as they would
  // overwrite manual entries with HSV-generated colors.
  const numValues = 256;
  const colors: number[][] = [];
  for (let i = 0; i < numValues; i++) {
    const t = i / (numValues - 1);
    const [r, g, b] = lookupCoolToWarm(t);
    colors.push([
      Math.round(r * 255),
      Math.round(g * 255),
      Math.round(b * 255),
    ]);
  }
  lut.setTable(colors);

  // setRange = setMappingRange — sets the scalar range for mapping
  lut.setRange?.(min, max);
  lut.setMappingRange?.(min, max);
  lut.modified?.();

  mapper.setScalarRange(min, max);
  mapper.setLookupTable?.(lut);
  mapper.modified?.();

  return lut;
}

export function replaceScalarBar(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  vtk: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scene: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lut: any,
  label: string,
) {
  if (scene.scalarBarActor) {
    scene.renderer.removeActor2D(scene.scalarBarActor);
    scene.scalarBarActor.delete?.();
    scene.scalarBarActor = null;
  }

  if (!lut) return null;

  const scalarBarActor = vtk.vtkScalarBarActor.newInstance();
  scalarBarActor.setScalarsToColors(lut);
  scalarBarActor.setAxisLabel(label);

  scalarBarActor.setAxisTextStyle({
    fontFamily: 'Arial',
    fontColor: 'white',
    fontStyle: 'normal',
    fontWeight: 'bold',
  });

  scalarBarActor.setTickTextStyle({
    fontFamily: 'Arial',
    fontColor: 'white',
    fontStyle: 'normal',
    fontWeight: 'normal',
  });

  scalarBarActor.modified?.();

  scene.renderer.addActor2D(scalarBarActor);
  scene.scalarBarActor = scalarBarActor;

  return scalarBarActor;
}
